import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { UsersService, type User, type UpdateUserDto } from '../../services/users/users.service';

const editUserSchema = z.object({
  nombreCompleto: z.string().min(1, 'Nombre completo es requerido').min(2, 'Mínimo 2 caracteres'),
  tipoUsuario: z.enum(['administrador', 'cliente', 'vendedor']),
  estado: z.boolean(),
  requiereCambioContrasena: z.boolean()
});

type EditUserFormData = z.infer<typeof editUserSchema>;

interface EditUserDrawerProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess?: () => void;
  onUpdateResult?: (success: boolean, message: string) => void;
}

export const EditUserDrawer: React.FC<EditUserDrawerProps> = ({
  isOpen,
  user,
  onClose,
  onSuccess,
  onUpdateResult
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema)
  });

  const tipoUsuario = watch('tipoUsuario');

  useEffect(() => {
    if (isOpen && user) {
      reset({
        nombreCompleto: user.nombreCompleto,
        tipoUsuario: user.tipoUsuario,
        estado: user.estado,
        requiereCambioContrasena: user.requiereCambioContrasena
      });
      
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      // Reset state when closing
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setSuccessMessage(null);
        reset();
      }, 300);
    }
  }, [isOpen, user, reset]);

  const handleClose = () => {
    setIsVisible(false);
    // Llamar onClose después de un pequeño delay para permitir la animación
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: EditUserFormData) => {
    if (!user) return;
    
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    
    try {
      const updateData = {
        nombreCompleto: data.nombreCompleto,
        tipoUsuario: data.tipoUsuario,
        estado: data.estado,
        requiereCambioContrasena: data.requiereCambioContrasena
      };

      await UsersService.updateUser(user.id, updateData);
      
      setSuccessMessage('Usuario actualizado exitosamente');
      
      // Notificar al componente padre del éxito
      if (onUpdateResult) {
        onUpdateResult(true, `Los datos de ${user.nombreCompleto} fueron actualizados exitosamente.`);
      }
      
      // Si fue exitoso y hay callback, ejecutarlo
      if (onSuccess) {
        onSuccess();
      }
      
      // Cerrar el drawer después de un momento
      setTimeout(() => {
        handleClose();
      }, 1000);
      
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al actualizar usuario';
      setErrorMessage(errorMsg);
      
      // Notificar al componente padre del error
      if (onUpdateResult) {
        onUpdateResult(false, errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const tiposUsuario = [
    { value: 'cliente', label: 'Cliente' },
    { value: 'vendedor', label: 'Vendedor' },
    { value: 'administrador', label: 'Administrador' }
  ];

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Editar Usuario
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Información del usuario */}
              <div className="mb-6">
                <div className="text-center py-6 bg-gradient-to-b from-gray-50 to-white rounded-lg">
                  <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-r from-purple-400 to-blue-400 flex items-center justify-center mb-4 shadow-lg">
                    <span className="text-white text-xl font-bold">
                      {user.nombreCompleto.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {user.nombreCompleto}
                  </h3>
                  <p className="text-gray-600">{user.email}</p>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full mt-2 ${
                    user.estado 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.estado ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>

              {/* Mensajes */}
              {errorMessage && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <X className="w-5 h-5 text-red-400 mr-3 shrink-0" />
                    <p className="text-red-800 text-sm">{errorMessage}</p>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <X className="w-5 h-5 text-green-400 mr-3 shrink-0" />
                    <p className="text-green-800 text-sm">{successMessage}</p>
                  </div>
                </div>
              )}

              {/* Instrucciones */}
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• El email no puede ser modificado por seguridad</li>
                    <li>• Los cambios se aplicarán inmediatamente</li>
                    <li>• El usuario recibirá notificación de los cambios</li>
                  </ul>
                </div>
              </div>

              {/* Formulario */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Información básica */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                    Información Básica
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        {...register('nombreCompleto')}
                        placeholder="Juan Carlos Pérez"
                        className={`w-full px-4 py-3 border rounded-lg transition-all ${
                          errors.nombreCompleto
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                        } focus:ring-4 focus:outline-none`}
                      />
                      {errors.nombreCompleto && (
                        <p className="mt-1 text-sm text-red-500">{errors.nombreCompleto.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={user.email}
                          disabled
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                            No editable
                          </span>
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        El email no puede modificarse por motivos de seguridad
                      </p>
                    </div>
                  </div>
                </div>

                {/* Configuración de acceso */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                    Configuración de Acceso
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Usuario *
                      </label>
                      <select
                        {...register('tipoUsuario')}
                        className={`w-full px-4 py-3 border rounded-lg transition-all ${
                          errors.tipoUsuario
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                        } focus:ring-4 focus:outline-none`}
                      >
                        {tiposUsuario.map(tipo => (
                          <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                        ))}
                      </select>
                      {errors.tipoUsuario && (
                        <p className="mt-1 text-sm text-red-500">{errors.tipoUsuario.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          {...register('estado')}
                          className="w-4 h-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Usuario activo</span>
                          <p className="text-xs text-gray-500">El usuario puede acceder al sistema</p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          {...register('requiereCambioContrasena')}
                          className="w-4 h-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Requiere cambio de contraseña</span>
                          <p className="text-xs text-gray-500">El usuario deberá cambiar su contraseña en el próximo inicio de sesión</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Información del sistema */}
                <div className="pt-4 border-t border-gray-200">
                  <details className="cursor-pointer">
                    <summary className="text-sm font-medium text-gray-600 hover:text-gray-900">
                      Información del Sistema
                    </summary>
                    <div className="mt-3 space-y-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                      <div><span className="font-medium">ID:</span> {user.id}</div>
                      <div><span className="font-medium">Creado:</span> {new Date(user.creadoEn).toLocaleString('es-ES')}</div>
                      <div><span className="font-medium">Actualizado:</span> {new Date(user.actualizadoEn).toLocaleString('es-ES')}</div>
                      {user.ultimoAcceso && (
                        <div><span className="font-medium">Último acceso:</span> {new Date(user.ultimoAcceso).toLocaleString('es-ES')}</div>
                      )}
                    </div>
                  </details>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading || !!successMessage}
                  className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
                >
                  {isLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>
                    {isLoading ? 'Actualizando...' : successMessage ? 'Actualizado ✓' : 'Actualizar Usuario'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EditUserDrawer;